<template>
    <div class="diagnostic-summary-card">
        <div class="summary-content">
            <!-- Calificación y avance -->
            <div class="rating-section">
                <h6 class="section-title">Avance ponderado</h6>
                <div class="rating-display">
                    <span class="rating-value">{{ averageRating }}</span>
                    <span class="rating-max">/ {{ maxRating }}</span>
                </div>
                <div class="progress-bar-container">
                    <div class="progress">
                        <div 
                            class="progress-bar" 
                            :style="{ 
                                width: ratingPercentage + '%',
                                backgroundColor: ratingBarColor
                            }"
                        ></div>
                    </div>
                </div>
                <p class="progress-text">
                    Avance: {{ progressPercentage }}% - {{ evaluatedCount }} de {{ totalCount }} ítems evaluados
                </p>
            </div>

            <!-- Distribución por nivel -->
            <div class="distribution-section">
                <h6 class="section-title">Distribución por nivel</h6>
                <div class="distribution-grid">
                    <div 
                        v-for="rating in summary" 
                        :key="rating.rating_id"
                        class="distribution-item"
                    >
                        <span 
                            class="rating-dot" 
                            :style="{ backgroundColor: getRatingColor(rating.rating_id) }"
                        ></span>
                        <span class="rating-name">{{ rating.name }}:</span>
                        <span class="rating-count">{{ rating.count }}</span>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'DiagnosticSummary',
    props: {
        summary: {
            type: Array,
            required: true,
            default: () => []
        },
        pillars: {
            type: Array,
            default: () => []
        },
        totalCount: {
            type: Number,
            default: 0
        },
        ratingDefinitions: {
            type: Array,
            default: () => []
        }
    },
    computed: {
        sortedRatingDefinitions() {
            if (!this.ratingDefinitions || !this.ratingDefinitions.length) return [];
            return [...this.ratingDefinitions].sort((a, b) => (a.value || 0) - (b.value || 0));
        },
        ratingLookup() {
            const map = {};
            if (this.ratingDefinitions && this.ratingDefinitions.length) {
                this.ratingDefinitions.forEach(def => {
                    map[def.id] = def;
                });
            }
            return map;
        },
        evaluatedCount() {
            if (!this.pillars || !this.pillars.length) return 0;
            let count = 0;
            this.pillars.forEach(pillar => {
                if (!pillar.elements) return;
                pillar.elements.forEach(element => {
                    if (!element.general_items) return;
                    element.general_items.forEach(generalItem => {
                        if (!generalItem.items) return;
                        generalItem.items.forEach(item => {
                            if (this.isSyncedEvaluated(item)) {
                                count++;
                            }
                        });
                    });
                });
            });
            return count;
        },
        
        progressPercentage() {
            if (!this.totalCount) return 0;
            return ((this.evaluatedCount / this.totalCount) * 100).toFixed(1);
        },
        
        averageRating() {
            const stats = this.calculateGlobalAverage();
            return stats.count > 0 ? stats.average.toFixed(2) : '0.00';
        },
        
        maxRating() {
            if (this.sortedRatingDefinitions.length) {
                const last = this.sortedRatingDefinitions[this.sortedRatingDefinitions.length - 1];
                return last && typeof last.value === 'number' ? last.value : 5;
            }

            if (!this.summary || this.summary.length === 0) return 5;
            const maxRatingObj = this.summary.reduce((max, rating) => {
                return rating.value > max.value ? rating : max;
            }, this.summary[0]);
            return maxRatingObj.value || 5;
        },
        
        ratingPercentage() {
            const rating = parseFloat(this.averageRating);
            const max = this.maxRating;
            
            if (max === 0) return 0;
            return ((rating / max) * 100).toFixed(1);
        },
        
        ratingBarColor() {
            const rating = parseFloat(this.averageRating);
            if (this.sortedRatingDefinitions.length) {
                let match = this.sortedRatingDefinitions[0];
                this.sortedRatingDefinitions.forEach(def => {
                    if (typeof def.value === 'number' && rating >= def.value) {
                        match = def;
                    }
                });
                return match && match.color ? match.color : '#6c757d';
            }

            // Fallback al mapa legado 0-5
            if (rating >= 0 && rating < 1) {
                return '#6c757d';
            } else if (rating >= 1 && rating < 2) {
                return '#dc3545';
            } else if (rating >= 2 && rating < 3) {
                return '#fd7e14';
            } else if (rating >= 3 && rating < 4) {
                return '#ffc107';
            } else if (rating >= 4 && rating < 5) {
                return '#17a2b8';
            } else if (rating >= 5) {
                return '#28a745';
            }
            return '#6c757d';
        }
    },
    methods: {
        getRatingColor(ratingId) {
            if (this.ratingDefinitions && this.ratingDefinitions.length) {
                const match = this.ratingDefinitions.find(r => r.id === ratingId);
                if (match && match.color) {
                    return match.color;
                }
            }

            const colors = {
                1: '#9CA3AF',
                2: '#EF4444',
                3: '#F97316',
                4: '#EAB308',
                5: '#3B82F6',
                6: '#10B981'
            };
            return colors[ratingId] || '#9CA3AF';
        },
        
        countItemsInPillar(pillar) {
            let count = 0;
            if (!pillar.elements) return count;
            
            pillar.elements.forEach(element => {
                if (!element.general_items) return;
                element.general_items.forEach(generalItem => {
                    if (!generalItem.items) return;
                    count += generalItem.items.length;
                });
            });
            
            return count;
        },
        
        countEvaluatedItemsInPillar(pillar) {
            let count = 0;
            if (!pillar.elements) return count;
            
            pillar.elements.forEach(element => {
                if (!element.general_items) return;
                element.general_items.forEach(generalItem => {
                    if (!generalItem.items) return;
                    generalItem.items.forEach(item => {
                        if (this.isItemEvaluated(item)) {
                            count++;
                        }
                    });
                });
            });
            
            return count;
        },
        
        calculateGlobalAverage() {
            let sum = 0;
            let count = 0;
            if (!this.pillars) {
                return { average: 0, count: 0 };
            }

            this.pillars.forEach(pillar => {
                if (!pillar.elements) return;
                pillar.elements.forEach(element => {
                    if (!element.general_items) return;
                    element.general_items.forEach(generalItem => {
                        if (!generalItem.items) return;
                        generalItem.items.forEach(item => {
                            if (this.hasRatedResponse(item)) {
                                const value = this.getRatingValue(item.response.rating);
                                if (typeof value === 'number') {
                                    sum += value;
                                    count++;
                                }
                            }
                        });
                    });
                });
            });

            return {
                average: count > 0 ? sum / count : 0,
                count
            };
        },

        hasRatedResponse(item) {
            if (!item || !item.response || !item.response.rating) {
                return false;
            }
            return !this.isNotApplicable(item.response.rating.id);
        },

        isSyncedEvaluated(item) {
            return !!(item && item.response && item.response.is_synced && item.response.rating);
        },

        getRatingValue(rating) {
            if (!rating) return null;
            const lookup = this.ratingLookup[rating.id];
            if (lookup && typeof lookup.value === 'number') {
                return lookup.value;
            }
            if (typeof rating.rating === 'number') {
                return rating.rating;
            }
            return null;
        },

        isNotApplicable(ratingId) {
            if (!ratingId) return false;
            const lookup = this.ratingLookup[ratingId];
            if (lookup) {
                return !!lookup.is_not_applicable;
            }
            return ratingId === 1;
        }
    }
};
</script>

<style scoped>
.diagnostic-summary-card {
    background: white;
    border: 1px solid #E5E7EB;
    border-radius: 8px;
    padding: 16px;
    margin-bottom: 16px;
}

.summary-content {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
    align-items: center;
}

.section-title {
    font-size: 12px;
    font-weight: 600;
    color: #6B7280;
    margin-bottom: 8px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

/* Sección de calificación */
.rating-section {
    border-right: 1px solid #E5E7EB;
    padding-right: 16px;
}

.rating-display {
    display: flex;
    align-items: baseline;
    margin-bottom: 6px;
}

.rating-value {
    font-size: 36px;
    font-weight: 700;
    color: #1F2937;
    line-height: 1;
}

.rating-max {
    font-size: 18px;
    font-weight: 400;
    color: #9CA3AF;
    margin-left: 4px;
}

.progress-bar-container {
    margin-bottom: 4px;
}

.progress {
    height: 6px;
    background-color: #E5E7EB;
    border-radius: 4px;
    overflow: hidden;
}

.progress-bar {
    transition: width 0.3s ease, background-color 0.3s ease;
}

.progress-text {
    font-size: 12px;
    color: #6B7280;
    margin: 0;
}

/* Sección de distribución */
.distribution-section {
    padding-left: 16px;
}

.distribution-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 8px;
}

.distribution-item {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 13px;
}

.rating-dot {
    width: 12px;
    height: 12px;
    border-radius: 50%;
    flex-shrink: 0;
}

.rating-name {
    color: #6B7280;
    flex: 1;
}

.rating-count {
    font-weight: 600;
    color: #1F2937;
}

/* Responsive */
@media (max-width: 768px) {
    .summary-content {
        grid-template-columns: 1fr;
        gap: 24px;
    }
    
    .rating-section {
        border-right: none;
        border-bottom: 1px solid #E5E7EB;
        padding-right: 0;
        padding-bottom: 24px;
    }
    
    .distribution-section {
        padding-left: 0;
    }
    
    .distribution-grid {
        grid-template-columns: 1fr;
    }
}
</style>
